import type {
  OrgEnablementStatus,
  PartnerClientStatus,
  PartnerClientWithSecret,
  Scope
} from '@epilot/sdk/identity'
import { ReactNode, useState } from 'react'
import { Outlet } from 'react-router-dom'

import { SCOPES } from './validation'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { config } from '@/configs/api.config'
import { useAuth } from '@/providers/AuthContextProvider'
import { isPermitted, Permissions } from '@/utils/permission-manager'

export const EPILOT_IDP_PATH = '/epilot-idp'

export const EpilotIdpAccess = () => {
  const { userData } = useAuth()

  // Guard the nested detail/create routes too, not only the sidebar item.
  if (!userData || !isPermitted(Permissions.MANAGE_EPILOT_IDP, userData)) {
    return (
      <ErrorNotice message="Managing Epilot IdP applications is available to admin and super-admin users." />
    )
  }
  if (!config.IDENTITY)
    return (
      <ErrorNotice message="Identity API is not configured for this stage." />
    )

  return <Outlet />
}

export const ErrorNotice = ({ message }: { message?: string }) =>
  message ? (
    <Alert variant="destructive">
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  ) : null

export const errorMessage = (error: unknown) =>
  error instanceof Error
    ? error.message
    : 'The request failed. Please try again.'

export const Field = ({
  label,
  id,
  hint,
  children
}: {
  label: string
  id: string
  hint?: string
  children: ReactNode
}) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    {children}
    {hint && <p className="text-sm text-muted-foreground">{hint}</p>}
  </div>
)

export const StatusBadge = ({
  status
}: {
  status: PartnerClientStatus | OrgEnablementStatus
}) => (
  <Badge
    variant={
      status === 'active'
        ? 'default'
        : status === 'suspended'
        ? 'destructive'
        : status === 'draft'
        ? 'outline'
        : 'secondary'
    }
  >
    {status}
  </Badge>
)

export const ScopePicker = ({
  value,
  onChange,
  allowed = SCOPES,
  disabled = false
}: {
  value: Scope[]
  onChange: (value: Scope[]) => void
  allowed?: readonly Scope[]
  disabled?: boolean
}) => (
  <fieldset className="space-y-2" disabled={disabled}>
    <legend className="mb-2 text-sm font-medium">Allowed scopes</legend>
    <div className="flex gap-6">
      {SCOPES.map((scope) => (
        <label className="flex items-center gap-2 text-sm" key={scope}>
          <Checkbox
            checked={value.includes(scope)}
            disabled={
              disabled ||
              scope === 'openid' ||
              (!allowed.includes(scope) && !value.includes(scope))
            }
            onCheckedChange={(checked) =>
              onChange(
                checked
                  ? [...value, scope]
                  : value.filter((item) => item !== scope)
              )
            }
          />
          {scope}
        </label>
      ))}
    </div>
  </fieldset>
)

// Secrets live only in component state. Never put these responses in React Query,
// localStorage, URLs, logs or toasts; creation and rotation are not auto-retried.
export const SecretDialog = ({
  client,
  onClose
}: {
  client: PartnerClientWithSecret
  onClose: () => void
}) => {
  const [copied, setCopied] = useState(false)
  const [copyError, setCopyError] = useState('')
  const [visible, setVisible] = useState(false)

  return (
    <Dialog
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      open
    >
      <DialogContent
        className="text-primary"
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-primary">
            Save the client secret
          </DialogTitle>
          <DialogDescription>
            This secret is shown once. Copy it to secure storage before closing.
            It cannot be retrieved later.
          </DialogDescription>
        </DialogHeader>
        <Field id="secret-client-id" label="Client ID">
          <Input id="secret-client-id" readOnly value={client.client_id} />
        </Field>
        <Field id="client-secret" label="Client secret">
          <Input
            autoComplete="off"
            id="client-secret"
            readOnly
            type={visible ? 'text' : 'password'}
            value={client.client_secret}
          />
        </Field>
        <Button onClick={() => setVisible(!visible)} variant="outline">
          {visible ? 'Hide secret' : 'Show secret'}
        </Button>
        {client.previous_secret_expires_at && (
          <p className="text-sm text-muted-foreground">
            Previous secret expires:{' '}
            {new Date(client.previous_secret_expires_at).toLocaleString()}
          </p>
        )}
        <ErrorNotice message={copyError} />
        <DialogFooter>
          <Button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(client.client_secret)
                setCopied(true)
                setCopyError('')
              } catch {
                setCopyError(
                  'Clipboard access failed. Use Show secret, then select and copy it from the field.'
                )
              }
            }}
            variant="outline"
          >
            {copied ? 'Copied' : 'Copy secret'}
          </Button>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export const ConfirmAction = ({
  title,
  description,
  destructive = false,
  askReason = false,
  children,
  onConfirm,
  onClose
}: {
  title: string
  description: string
  destructive?: boolean
  askReason?: boolean
  children?: ReactNode
  onConfirm: (reason: string) => Promise<void>
  onClose: () => void
}) => {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  return (
    <Dialog
      onOpenChange={(open) => {
        if (!open && !busy) onClose()
      }}
      open
    >
      <DialogContent className="text-primary">
        <DialogHeader>
          <DialogTitle className="text-primary">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {askReason && (
          <Field id="action-reason" label="Reason (optional)">
            <Input
              disabled={busy}
              id="action-reason"
              maxLength={1024}
              onChange={(e) => setReason(e.target.value)}
              value={reason}
            />
          </Field>
        )}
        <fieldset disabled={busy}>{children}</fieldset>
        <ErrorNotice message={error} />
        <DialogFooter>
          <Button disabled={busy} onClick={onClose} variant="outline">
            Cancel
          </Button>
          <Button
            loading={busy}
            onClick={async () => {
              setBusy(true)
              setError('')
              try {
                await onConfirm(reason)
                onClose()
              } catch (err) {
                setError(errorMessage(err))
              } finally {
                setBusy(false)
              }
            }}
            variant={destructive ? 'destructive' : 'default'}
          >
            {title}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
